import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Spinner } from 'reactstrap';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import { FiX } from 'react-icons/fi';
import { Portal } from '@components/Portal';
import { ScreenshotViewerHeader } from '@components/ScreenshotViewerHeader';
import { ScreenshotThumbnailStrip } from '@components/ScreenshotThumbnailStrip';
import { useSetSupportWidgetHidden } from '@/contexts/SupportWidget.context';
import {
  getScreenshotFullSrc,
  getScreenshotPreviewSrc,
} from '@/utils/screenshotPrivacy';
import styles from './Timeline.module.scss';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';

/**
 * Full-screen screenshot viewer for a timeline session: custom light-themed
 * header + thumbnail strip (own prev/next arrows, active-thumbnail badge)
 * layered into yet-another-react-lightbox's `render.controls` slot, with
 * the library's own zoom/counter/fullscreen and default main-image nav
 * arrows left untouched. Session activities load asynchronously after the
 * modal opens, so a loading/error/empty overlay covers that gap before the
 * Lightbox takes over.
 */
export const TimelineScreenshotsLightbox = ({
  open,
  onClose,
  activitiesLoading,
  activitiesError,
  flattenedScreens,
  selectedScreenshotIndex,
  setSelectedScreenshotIndex,
  selectedTimezone,
  activeSessionMeta,
  employeeName,
  teamName,
}) => {
  const fullscreenRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const setSupportWidgetHidden = useSetSupportWidgetHidden();

  useEffect(() => {
    setSupportWidgetHidden(open);
    return () => setSupportWidgetHidden(false);
  }, [open, setSupportWidgetHidden]);

  const slides = useMemo(
    () =>
      flattenedScreens.map((shot) => ({
        src: getScreenshotFullSrc(shot),
        thumbnail: getScreenshotPreviewSrc(shot),
      })),
    [flattenedScreens]
  );

  const thumbnailItems = useMemo(
    () =>
      flattenedScreens.map((shot, index) => ({
        id: shot.revealKey || shot.activityId || index,
        previewSrc: getScreenshotPreviewSrc(shot),
        label: shot.timestamp
          ? moment(shot.timestamp).tz(selectedTimezone).format('hh:mm A')
          : '',
      })),
    [flattenedScreens, selectedTimezone]
  );

  const activeShot = flattenedScreens[selectedScreenshotIndex];
  const currentSrc = slides[selectedScreenshotIndex]?.src;
  const showLightbox =
    open && !activitiesLoading && !activitiesError && slides.length > 0;
  const showStatusModal =
    open && (activitiesLoading || !!activitiesError || slides.length === 0);

  const handleDownload = async () => {
    if (!currentSrc) return;
    try {
      const response = await fetch(currentSrc);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `screenshot-${activeSessionMeta?.dayLabel || 'timeline'}-${
        selectedScreenshotIndex + 1
      }.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download screenshot', error);
    }
  };

  const handleToggleFullscreen = () => {
    if (isFullscreen) {
      fullscreenRef.current?.exit();
    } else {
      fullscreenRef.current?.enter();
    }
  };

  return (
    <>
      {showStatusModal && (
        <Portal>
          <div className={styles.screenshotsLoadingOverlay}>
            <button
              type="button"
              className={styles.screenshotsLoadingClose}
              onClick={onClose}
              aria-label="Close"
            >
              <FiX size={22} />
            </button>
            {activitiesLoading && <Spinner color="primary" />}
            {!activitiesLoading && activitiesError && (
              <div className={styles.screenshotsLoadingMessage}>
                {activitiesError}
              </div>
            )}
            {!activitiesLoading && !activitiesError && slides.length === 0 && (
              <div className={styles.screenshotsLoadingMessage}>
                No screenshots available for this session.
              </div>
            )}
          </div>
        </Portal>
      )}

      <Lightbox
        key={activeSessionMeta?.sessionId || 'timeline-lightbox'}
        open={showLightbox}
        close={onClose}
        index={selectedScreenshotIndex}
        slides={slides}
        on={{
          view: ({ index }) => setSelectedScreenshotIndex(index),
          enterFullscreen: () => setIsFullscreen(true),
          exitFullscreen: () => setIsFullscreen(false),
        }}
        plugins={[Counter, Zoom, Fullscreen]}
        carousel={{ finite: true, spacing: 0, padding: 0 }}
        fullscreen={{ ref: fullscreenRef }}
        className={styles.lightboxLight}
        toolbar={{ buttons: [] }}
        render={{
          controls: () => (
            <>
              <ScreenshotViewerHeader
                title="Screenshot"
                dateLabel={
                  activeShot?.timestamp
                    ? moment(activeShot.timestamp)
                        .tz(selectedTimezone)
                        .format('MMM D, YYYY')
                    : activeSessionMeta?.dayLabel
                }
                timeLabel={
                  activeShot?.timestamp
                    ? moment(activeShot.timestamp)
                        .tz(selectedTimezone)
                        .format('hh:mm A')
                    : undefined
                }
                employeeName={employeeName}
                teamName={teamName}
                onDownload={handleDownload}
                imageHref={currentSrc}
                onToggleFullscreen={handleToggleFullscreen}
                isFullscreen={isFullscreen}
                onClose={onClose}
                className={styles.lightboxHeaderPinned}
              />
              <ScreenshotThumbnailStrip
                items={thumbnailItems}
                selectedIndex={selectedScreenshotIndex}
                onSelect={setSelectedScreenshotIndex}
                className={styles.lightboxStripPinned}
              />
            </>
          ),
        }}
      />
    </>
  );
};
